import RoundButton from "features/shared/components/buttons/RoundButton";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import React from "react";
import InvoiceSimplifiedDTO from "../dtos/InvoiceSimplifiedDTO";
import InvoiceInfo from "./InvoiceInfo";
import InvoiceStateInfo from "./InvoiceStateInfo";
import InvoiceSimplifedPrices from "./InvoiceSimplifedPrices";
import OverlayButton from "features/shared/components/buttons/OverlayButton";
import styles from "./invoicesimplified.module.scss";
import { getInvoiceDetailRoute } from "features/shared/helpers/routeHelper";
import { useTranslation } from "react-i18next";
import DocumentDownloadButton from "features/shared/components/documents/DocumentDownloadButton";
import DocumentSimplifiedDTO from "../dtos/DocumentSimplifiedDTO";

interface Props {
  invoice: InvoiceSimplifiedDTO;
}

const InvoiceSimplified = ({ invoice }: Props) => {
  // translations
  const tLabels = useTranslation("invoice\\invoice").t;
  const tTerms = useTranslation("shared\\terms").t;

  // helpers
  const getInvocieDownloadTitle = (document: DocumentSimplifiedDTO): string => { 
    let extension = document.name?.split('.')?.pop()?.toLocaleUpperCase();
    return `${tTerms("Download")} ${extension}`;
  }

  // sub-renders
  const renderFromToDates = () => {
    if (invoice.timePeriodFrom && invoice.timePeriodTo) {
      return (
        <div className="d-none d-xxl-block small text-muted">
          <DateFormatter date={invoice.timePeriodFrom} /> -{" "}
          <DateFormatter date={invoice.timePeriodTo} />
        </div>
      );
    }
  };

  const renderInvoiceStateInfo = () => {
    if (invoice.invoiceState == "Concept") {
      return <>-</>;
    } else {
      return (
        <InvoiceStateInfo
          state={invoice.invoiceState!}
          dueDate={invoice.dueDate!}
        />
      );
    }
  };

  const renderPaymentScheduleNumber = () => {
    if (invoice.paymentScheduleNumber) {
      return (
        <div className="d-none d-xl-block small">
          {tLabels("invoiceSimplified.paymentScheduleNumber")}
          {invoice.paymentScheduleNumber}
        </div>
      );
    }
  };

  // render
  return (
    <OverlayButton
      overlayClass="d-sm-none"
      asLink
      to={getInvoiceDetailRoute(invoice.invoiceType!, invoice.invoiceId!, invoice.proformaInvoiceId)}
    >
      <div className={`row align-items-center no-gutters ${styles.row}`}>
        <div className="col">
          <div className="row no-gutters align-items-center">
            <div className="col col-sm-8 col-lg-7 col-xl-8 col-xxl-9">
              <div className="row no-gutters align-items-center">
                <div className="col-sm-7 col-lg-7 col-xl-5 col-xxl-4 order-1 order-sm-0 pr-sm-3">
                  <InvoiceInfo
                    invoiceType={invoice.invoiceType!}
                    invoiceSubType={invoice.invoiceSubType!}
                    commodityDetails={invoice.commodityDetail!}
                    entityLinks={invoice.entityLinks!}
                  />
                </div>
                <div className="col-sm-5 col-lg-5 col-xl-7 col-xxl-8 pl-2 pl-sm-0 mb-1 mb-md-0">
                  <div className="row no-gutters align-items-lg-center ">
                    <div className="col-xl-6 col-xxl-4 pr-lg-3 pr-xl-0">
                      <div className="text-muted text-truncate ">
                        {invoice.invoiceNumber}
                      </div>
                      {renderPaymentScheduleNumber()}
                    </div>
                    <div className="d-none d-xxl-block col-xxl-3 text-muted">
                      {invoice.variableSymbol}
                    </div>
                    <div
                      className={`col-xl-6 col-xxl-5 pr-lg-3 pr-xl-0 pl-xxl-4 pl-xxxl-5 ${styles.dueDateItem}`}
                    >
                      <div>
                        <span>{tLabels("invoiceSimplified.to")}</span>
                        <DateFormatter date={invoice.dueDate!} />
                      </div>
                      {renderFromToDates()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-auto col-sm-4 col-lg-5 col-xl-4 col-xxl-3 pl-3 pl-lg-0">
              <div className="row flex-column flex-lg-row no-gutters">
                <div className="col-auto col-lg-7 text-right text-sm-left">
                  <InvoiceSimplifedPrices
                    totalAmount={invoice.totalAmount!}
                    unpaidAmount={invoice.unpaidAmount!}
                    dueDate={invoice.dueDate!}
                    currency={invoice.currency!}
                  />
                </div>
                <div className="col-auto col-lg-5 mt-1 mt-lg-0">
                  <div className="d-flex align-items-center justify-content-end justify-content-sm-start h-100">
                    {renderInvoiceStateInfo()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`d-none d-sm-block col-auto pl-3 ${styles.btnCol}`}>
          <div className="d-flex flex-column flex-lg-row align-items-end align-items-lg-center justify-content-lg-end">
            {invoice.documentSimplified !== null && (
              <div className="mb-2 mb-lg-0 mr-lg-2">
                <DocumentDownloadButton 
                  id={invoice.invoiceId!}
                  icon="file-pdf"
                  documents={invoice.documentSimplified!}
                  formatTitle={getInvocieDownloadTitle}
                />
              </div>
            )}
            <div>
              <RoundButton
                asLink
                to={getInvoiceDetailRoute(
                  invoice.invoiceType!,
                  invoice.invoiceId!,
                  invoice.proformaInvoiceId
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </OverlayButton>
  );
};

export default InvoiceSimplified;
